// /components/ui/shape-landing-hero.js
"use client";

import { motion } from "framer-motion";
import { Circle } from "lucide-react";
import { cn } from "../../lib/utils";
import { useNavigate } from "react-router-dom";

// Inside your component:
function ElegantShape({
  className,
  delay = 0,
  width = 500,
  height = 200,
  rotate = 0,
  gradient = "from-slate-500/[0.08]",
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: -150,
        rotate: rotate - 15,
      }}
      animate={{
        opacity: 1,
        y: 0,
        rotate: rotate,
      }}
      transition={{
        duration: 2.4,
        delay,
        ease: [0.23, 0.86, 0.39, 0.96],
        opacity: { duration: 1.2 },
      }}
      className={cn("absolute", className)}
    >
      <motion.div
        animate={{
          y: [0, 15, 0],
        }}
        transition={{
          duration: 12,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
        style={{
          width,
          height,
        }}
        className="relative"
      >
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-gradient-to-r to-transparent",
            gradient,
            "backdrop-blur-[2px] border-2 border-slate-200",
            "shadow-[0_8px_32px_0_rgba(100,116,139,0.1)]",
            "after:absolute after:inset-0 after:rounded-full",
            "after:bg-[radial-gradient(circle_at_50%_50%,rgba(100,116,139,0.2),transparent_70%)]"
          )}
        />
      </motion.div>
    </motion.div>
  );
}

function HeroGeometric({
  badge = "Design Collective",
  // title1 = "Elevate Your Digital Vision",
  title2 = "Crafting Exceptional Websites",
}) {
  const fadeUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.2,
        duration: 0.8,
      },
    }),
  };
  const navigate = useNavigate();
  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-slate-50">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100/30 via-transparent to-rose-100/50 blur-3xl" />

      <div className="absolute inset-0 overflow-hidden">
        <ElegantShape
          delay={0.3}
          width={600}
          height={140}
          rotate={12}
          gradient="from-blue-500/[0.15]"
          className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]"
        />

        <ElegantShape
          delay={0.5}
          width={500}
          height={120}
          rotate={-15}
          gradient="from-rose-500/[0.15]"
          className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]"
        />

        <ElegantShape
          delay={0.4}
          width={300}
          height={80}
          rotate={-8}
          gradient="from-violet-500/[0.15]"
          className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]"
        />

        <ElegantShape
          delay={0.6}
          width={200}
          height={60}
          rotate={20}
          gradient="from-amber-500/[0.15]"
          className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]"
        />

        <ElegantShape
          delay={0.7}
          width={150}
          height={40}
          rotate={-25}
          gradient="from-sky-500/[0.15]"
          className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]"
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            custom={0}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-500/[0.03] border border-slate-200 mb-8 md:mb-12"
          >
            <Circle className="h-2 w-2 fill-rose-500" />
            <span className="text-sm text-slate-600 tracking-wide">
              {badge}
            </span>
          </motion.div>

          <motion.div
            custom={1}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
            className="flex justify-center"
          >
            <h1 className="text-5xl sm:text-6xl md:text-5xl font-bold mb-6 md:mb-8 tracking-tight whitespace-nowrap text-ellipsis text-center mx-auto">
              <span className="text-[#4472c6]">{title2}</span>
            </h1>
          </motion.div>

          <motion.div
            custom={2}
            variants={fadeUpVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="text-xl sm:text-xl md:text-xxl text-slate-600 mb-8 leading-relaxed font-medium tracking-wide max-w-xl mx-auto px-4">
              with Real-Time AI Interviews Automate candidate screening,
              evaluate soft skills, and hire the best talent faster
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/dashboard?page=index=1"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black dark:bg-black hover:bg-gray-800 transition-colors duration-200 rounded-full text-white dark:text-white px-6 py-2.5 font-medium inline-flex items-center justify-center"
              >
                Go to App
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-transparent to-slate-50/80 pointer-events-none" />
    </div>
  );
}

export { HeroGeometric };
